import React, { useEffect, useState, useRef } from 'react';
import { Link, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { logout } from '../../store/slices/authSlice';
import api from '../../services/api';
import { unsubscribePush } from '../../services/pushNotifications';
import NotificationBell from './NotificationBell';

const SearchIcon = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

/* Part Picker glyph — parked with the nav entry below while the picker is
   being built out of sight. Identical to the landing page's.
const ExternalLinkIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M13 4H4v16h16v-9" />
    <path d="M15 3h6v6" />
    <path d="M21 3l-9 9" />
  </svg>
); */

const MessageIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
  </svg>
);

const HamburgerIcon = ({ open }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    {open
      ? <><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></>
      : <><line x1="3" y1="7" x2="21" y2="7" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="17" x2="21" y2="17" /></>
    }
  </svg>
);

/* Marketplace and My Garage are temporarily hidden from the navbar. Nav links
   only — the routes, pages, components and backend are all untouched and both
   remain reachable by direct URL. Restore by uncommenting the Marketplace entry
   below and the My Garage links in the desktop and drawer blocks. (They were
   originally pulled to free space for the Part Picker entry, which is itself
   hidden now, so the row has room again whenever you want them back.) */
const NAV_LINKS = [
  { to: '/feed',      label: 'Feed' },
  { to: '/events',    label: 'Events' },
  { to: '/collections', label: 'Collections' },
  // { to: '/marketplace', label: 'Marketplace' },
  { to: '/services',  label: 'Services' },
  { to: '/routes',    label: 'Routes + Landmarks' },
];

const Navbar = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user } = useSelector(s => s.auth);
  const [unreadMsgs, setUnreadMsgs] = useState(0);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const drawerRef = useRef();
  const hamburgerRef = useRef();

  // Close drawer on route change
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  // Close drawer on outside click — exclude the hamburger button itself
  // (the button's own onClick handles toggling; without this exclusion,
  //  mousedown closes the drawer and then onClick reopens it immediately)
  useEffect(() => {
    if (!drawerOpen) return;
    const handler = (e) => {
      if (hamburgerRef.current && hamburgerRef.current.contains(e.target)) return;
      if (drawerRef.current && !drawerRef.current.contains(e.target)) setDrawerOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [drawerOpen]);

  // Lock body scroll when drawer open
  useEffect(() => {
    document.body.style.overflow = drawerOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [drawerOpen]);

  useEffect(() => {
    if (!isAuthenticated) return;
    const fetchUnread = () => {
      api.get('/messages/conversations').then(r => {
        const total = r.data.reduce((sum, c) => sum + (c.unreadCount || 0), 0);
        setUnreadMsgs(total);
      }).catch(() => {});
    };
    fetchUnread();
    const id = setInterval(fetchUnread, 15000);
    return () => clearInterval(id);
  }, [isAuthenticated]);

  if (!isAuthenticated) return null;
  // Hide nav while the user is still on the post-OAuth username picker.
  if (user && user.usernameSetByUser === false) return null;

  return (
    <>
      <nav className="navbar">
        <div className="navbar-inner">
          {/* Logo */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
            <Link to="/events" className="navbar-brand" aria-label="Home">
              <img
                src="/images/logo-car.png"
                alt="MyCarCollection"
                style={{ height: 170, width: 'auto', objectFit: 'contain', opacity: 0.9, marginTop: 14 }}
              />
            </Link>
            <span style={{
              fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.08em',
              color: 'rgba(255,255,255,0.35)', border: '1px solid rgba(255,255,255,0.18)',
              padding: '2px 5px', borderRadius: 3, lineHeight: 1, alignSelf: 'center',
            }}>BETA</span>
          </div>

          {/* Desktop nav links */}
          <div className="navbar-links">
            {NAV_LINKS.map(l => <NavLink key={l.to} to={l.to}>{l.label}</NavLink>)}
            {/* Temporarily hidden — see NAV_LINKS above.
            <NavLink to={`/garage/${user?.id}`}>My Garage</NavLink> */}
          </div>

          {/* Right actions */}
          <div className="navbar-actions">
            {/* Part Picker is hidden from the UI while it's built behind the
                scenes — the route, page and data all still ship, so /partpicker
                works by direct URL. Restore by uncommenting this entry and the
                ExternalLinkIcon above. Sat outside the nav pill, before Search,
                because the picker is its own destination, not another tab.
            <NavLink to="/partpicker" className="navbar-partpicker navbar-desktop-only">
              <span>Part Picker</span>
              <ExternalLinkIcon />
            </NavLink> */}

            {/* Desktop search + messages */}
            <NavLink to="/search" className="navbar-search-link navbar-desktop-only">
              <span>Search</span>
              <SearchIcon />
            </NavLink>

            <NotificationBell />

            <NavLink to="/messages" className="navbar-msg-link" title="Messages">
              <MessageIcon />
              {unreadMsgs > 0 && (
                <span className="navbar-msg-badge">{unreadMsgs > 9 ? '9+' : unreadMsgs}</span>
              )}
            </NavLink>

            {/* Desktop avatar */}
            <div
              className="navbar-desktop-only"
              onClick={() => navigate(`/profile/${user?.username}`)}
              style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', padding: '4px 8px 4px 16px', borderLeft: '1px solid rgba(255,255,255,0.08)' }}
            >
              {user?.profileImageUrl
                ? <img src={user.profileImageUrl} alt="" className="navbar-avatar" />
                : <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#2c2c2e', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, color: '#ffffff', border: '1px solid #3a3a3c' }}>{user?.username?.[0]?.toUpperCase()}</div>}
              <span style={{ fontSize: '0.875rem', fontWeight: 600 }}>{user?.username}</span>
            </div>

            {/* Mobile hamburger */}
            <button
              ref={hamburgerRef}
              className="navbar-hamburger"
              onClick={() => setDrawerOpen(o => !o)}
              aria-label="Menu"
            >
              <HamburgerIcon open={drawerOpen} />
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile drawer overlay */}
      {drawerOpen && <div className="navbar-drawer-overlay" onClick={() => setDrawerOpen(false)} />}

      {/* Mobile drawer */}
      <div ref={drawerRef} className={`navbar-drawer${drawerOpen ? ' open' : ''}`}>
        {/* Profile row */}
        <div
          className="navbar-drawer-profile"
          onClick={() => { navigate(`/profile/${user?.username}`); }}
        >
          {user?.profileImageUrl
            ? <img src={user.profileImageUrl} alt="" className="navbar-avatar" />
            : <div style={{ width: 40, height: 40, borderRadius: '50%', background: '#2c2c2e', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, color: '#ffffff', border: '1px solid #3a3a3c', fontSize: '1.1rem' }}>{user?.username?.[0]?.toUpperCase()}</div>}
          <div>
            <div style={{ fontWeight: 700, fontSize: '0.95rem', color: '#ffffff' }}>{user?.username}</div>
            <div style={{ fontSize: '0.75rem', color: '#8e8e93' }}>View profile</div>
          </div>
        </div>

        <div className="navbar-drawer-divider" />

        {/* Nav links */}
        <nav className="navbar-drawer-links">
          {NAV_LINKS.map(l => (
            <NavLink key={l.to} to={l.to} className={({ isActive }) => `navbar-drawer-link${isActive ? ' active' : ''}`}>
              {l.label}
            </NavLink>
          ))}
          {/* Temporarily hidden — see NAV_LINKS above.
          <NavLink to={`/garage/${user?.id}`} className={({ isActive }) => `navbar-drawer-link${isActive ? ' active' : ''}`}>
            My Garage
          </NavLink> */}
        </nav>

        <div className="navbar-drawer-divider" />

        {/* Utility links */}
        <NavLink to="/search" className="navbar-drawer-link">
          Search
        </NavLink>
        <NavLink to="/messages" className="navbar-drawer-link">
          Messages {unreadMsgs > 0 && <span className="navbar-msg-badge" style={{ position: 'static', marginLeft: 6 }}>{unreadMsgs > 9 ? '9+' : unreadMsgs}</span>}
        </NavLink>

        <div className="navbar-drawer-divider" />

        <button
          className="navbar-drawer-link"
          style={{ color: '#e84545', width: '100%', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}
          onClick={async () => { await unsubscribePush(); dispatch(logout()); navigate('/login'); }}
        >
          Sign out
        </button>
      </div>
    </>
  );
};

export default Navbar;
